import { WorkItem } from '../api/workItems';

export const SOURCE_HOME_ROLE = 'source_home';

export function isSourceHomeItem(item: Pick<WorkItem, 'meta'> | null | undefined): boolean {
    const meta = item?.meta;
    if (!meta || typeof meta !== 'object') return false;
    return (meta as Record<string, unknown>).role === SOURCE_HOME_ROLE;
}

/** Verb tasks grouped under a source home stay off Today. Existing orphans have no flag. */
export function isTodayFamilyMember(item: Pick<WorkItem, 'meta'> | null | undefined): boolean {
    if (!item || isSourceHomeItem(item)) return false;
    const meta = item.meta;
    if (!meta || typeof meta !== 'object') return false;
    return (meta as Record<string, unknown>).today_family_member === true;
}

export function omitTodayFamilyMembers<T extends Pick<WorkItem, 'meta'>>(items: readonly T[]): T[] {
    return items.filter((item) => !isTodayFamilyMember(item));
}

function familyBatchId(item: Pick<WorkItem, 'meta'>): string {
    const meta = item.meta;
    if (!meta || typeof meta !== 'object') return '';
    const batchId = (meta as Record<string, unknown>).batch_id;
    return typeof batchId === 'string' ? batchId.trim() : '';
}

/**
 * Drop a deleted source home and clear the Today-hide flag on its steps.
 * The server does the same on DELETE. The page list has to match, or those
 * steps stay omitted until the next full reload.
 */
export function releaseLocalFamilyOnSourceHomeDelete<T extends Pick<WorkItem, 'id' | 'meta'>>(
    items: readonly T[],
    deletedId: string,
    deletedItem?: Pick<WorkItem, 'id' | 'meta'> | null,
): T[] {
    const listed = items.find((row) => row.id === deletedId);
    // Today only keeps TRIAGE/ACTIVE rows. An archived source home that was
    // opened from a fetch is not in that list, so the id alone cannot release
    // its steps. The fetched row carries the batch.
    const deleted = listed ?? (deletedItem && deletedItem.id === deletedId ? deletedItem : undefined);
    const remaining = items.filter((row) => row.id !== deletedId);
    if (!deleted || !isSourceHomeItem(deleted)) return remaining;
    const batchId = familyBatchId(deleted);
    if (!batchId) return remaining;
    return remaining.map((row) => {
        if (familyBatchId(row) !== batchId || !isTodayFamilyMember(row)) return row;
        const meta = { ...(row.meta as Record<string, unknown>) };
        delete meta.today_family_member;
        return { ...row, meta };
    });
}

/**
 * Whether the drawer should ask for the family. A bare batch_total without
 * batch_id cannot be opened, so it is not a family edge.
 */
export function mayHaveSourceFamily(item: Pick<WorkItem, 'meta'> | null | undefined): boolean {
    if (!item?.meta || typeof item.meta !== 'object') return false;
    const meta = item.meta as Record<string, unknown>;
    if (meta.role === SOURCE_HOME_ROLE) return true;
    if (meta.today_family_member === true) return true;
    const batchId = typeof meta.batch_id === 'string' ? meta.batch_id.trim() : '';
    if (!batchId) return false;
    const raw = meta.batch_total;
    const parsed = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() ? Number(raw) : NaN;
    return Number.isFinite(parsed) && Math.floor(parsed) > 1;
}
